/**
 * Admin size-config modal ↔ Azure VM infra sizing (contract §1 flat `vm_*` fields).
 * Form values keep numbers as strings so fields can be left blank.
 */
import type { AppriseDiskType, AppriseIdentityMode, VmSizing } from '@/types'

export const VM_DISK_TYPES: readonly AppriseDiskType[] = ['Premium_LRS', 'StandardSSD_LRS', 'Standard_LRS', 'Premium_ZRS', 'StandardSSD_ZRS']
export const VM_IDENTITY_MODES: readonly AppriseIdentityMode[] = ['system_assigned', 'user_assigned', 'none']

export interface VmSizingFormValues {
  vm_size: string
  vm_vcpu: string
  vm_memory_gb: string
  vm_os_disk_gb: string
  vm_os_disk_type: string
  vm_public_ip_default: boolean
  vm_identity_default: string
  vm_monitoring_default: boolean
  vm_backup_default: boolean
  vm_overrides_allowed: boolean
  /** The size's default operating system image (all blank = the portal default). */
  vm_image_publisher: string
  vm_image_offer: string
  vm_image_sku: string
  vm_image_version: string
  vm_image_label: string
  vm_image_os_type: string
  /** Users this size is meant for (blank = not set). */
  max_users: string
}

export interface VmSizingPayload {
  vm_size: string
  vm_vcpu: number | null
  vm_memory_gb: number | null
  vm_os_disk_gb: number | null
  vm_os_disk_type: string
  vm_public_ip_default: boolean
  vm_identity_default: string
  vm_monitoring_default: boolean
  vm_backup_default: boolean
  vm_overrides_allowed: boolean
  /** Sent for Apprise only: the default operating system image ('' everywhere clears it). */
  vm_image_publisher?: string
  vm_image_offer?: string
  vm_image_sku?: string
  vm_image_version?: string
  vm_image_label?: string
  vm_image_os_type?: string
  /** Sent for Apprise only; null clears it. */
  max_users?: number | null
}

export const VM_FORM_KEYS: ReadonlyArray<keyof VmSizingFormValues> = [
  'vm_size', 'vm_vcpu', 'vm_memory_gb', 'vm_os_disk_gb', 'vm_os_disk_type', 'vm_public_ip_default',
  'vm_identity_default', 'vm_monitoring_default', 'vm_backup_default', 'vm_overrides_allowed',
  'vm_image_publisher', 'vm_image_offer', 'vm_image_sku', 'vm_image_version', 'vm_image_label', 'vm_image_os_type', 'max_users',
]

const str = (n: number | null | undefined) => (n == null ? '' : String(n))

export function vmFormFromResources(profile: VmSizing | null | undefined, userCount?: number | null): VmSizingFormValues {
  // A derived profile is not stored: leave the form blank so saving the size does not turn it into an explicit one.
  const vm = profile?.derived ? null : profile
  return {
    vm_size: vm?.vm_size ?? '',
    vm_vcpu: str(vm?.vcpu),
    vm_memory_gb: str(vm?.memory_gb),
    vm_os_disk_gb: str(vm?.os_disk_gb),
    vm_os_disk_type: vm?.os_disk_type ?? 'StandardSSD_LRS',
    // New-profile defaults follow the seeded Apprise Small size (contract v2): public IP on, no identity, no monitoring.
    vm_public_ip_default: vm?.public_ip_default ?? true,
    vm_identity_default: vm?.identity_default ?? 'none',
    vm_monitoring_default: vm?.monitoring_default ?? false,
    vm_backup_default: vm?.backup_default ?? false,
    vm_overrides_allowed: vm?.overrides_allowed ?? true,
    // the default image is stored on the size itself, so it is shown even when the VM size is derived
    vm_image_publisher: profile?.image?.publisher ?? '',
    vm_image_offer: profile?.image?.offer ?? '',
    vm_image_sku: profile?.image?.sku ?? '',
    vm_image_version: profile?.image?.version ?? '',
    vm_image_label: profile?.image?.label ?? '',
    vm_image_os_type: profile?.image?.os_type ?? '',
    max_users: str(userCount),
  }
}

const num = (s: string): number | null => {
  const t = s.trim()
  if (!t) return null
  const n = Number(t)
  return Number.isFinite(n) ? n : null
}

export interface VmFormOptions {
  /** The size belongs to Apprise: the VM profile block is the whole editor (user count included). */
  apprise?: boolean
}

/**
 * Flat backend fields. For other products an empty VM size means "no VM profile" (resources.vm = null). For Apprise
 * an empty VM size is allowed: the profile's own vCPU / memory (and defaults) are kept and the VM size is derived.
 */
export function vmPayloadFromForm(v: VmSizingFormValues, opts: VmFormOptions = {}): VmSizingPayload {
  const size = v.vm_size.trim()
  if (!size && !opts.apprise) {
    return {
      vm_size: '', vm_vcpu: null, vm_memory_gb: null, vm_os_disk_gb: null, vm_os_disk_type: '',
      vm_public_ip_default: false, vm_identity_default: 'system_assigned', vm_monitoring_default: true,
      vm_backup_default: false, vm_overrides_allowed: v.vm_overrides_allowed,
    }
  }
  return {
    ...(opts.apprise ? {
      max_users: num(v.max_users),
      vm_image_publisher: v.vm_image_publisher.trim(),
      vm_image_offer: v.vm_image_offer.trim(),
      vm_image_sku: v.vm_image_sku.trim(),
      vm_image_version: v.vm_image_version.trim(),
      vm_image_label: v.vm_image_label.trim(),
      vm_image_os_type: v.vm_image_os_type.trim(),
    } : {}),
    vm_size: size,
    vm_vcpu: num(v.vm_vcpu),
    vm_memory_gb: num(v.vm_memory_gb),
    vm_os_disk_gb: num(v.vm_os_disk_gb),
    vm_os_disk_type: v.vm_os_disk_type,
    vm_public_ip_default: v.vm_public_ip_default,
    vm_identity_default: v.vm_identity_default,
    vm_monitoring_default: v.vm_monitoring_default,
    vm_backup_default: v.vm_backup_default,
    vm_overrides_allowed: v.vm_overrides_allowed,
  }
}

/**
 * Returns an error message, or null when valid. Other products: a blank VM size = VM profile disabled. Apprise: a blank
 * VM size is allowed (it is derived from the vCPU / memory), but whatever is entered must be valid.
 */
export function validateVmSizingForm(v: VmSizingFormValues, opts: VmFormOptions = {}): string | null {
  const p = vmPayloadFromForm(v, opts)
  if (opts.apprise && v.max_users.trim() && (p.max_users == null || !Number.isInteger(p.max_users) || p.max_users < 1))
    return 'User count must be a whole number ≥ 1'
  if (opts.apprise) {
    const parts = [v.vm_image_publisher, v.vm_image_offer, v.vm_image_sku].map((x) => x.trim())
    if (parts.some(Boolean) && (!parts.every(Boolean) || !['windows', 'linux'].includes(v.vm_image_os_type.trim())))
      return 'Pick the operating system from the list (or clear it to use the portal default)'
  }
  if (!p.vm_size && !opts.apprise) return null
  if (p.vm_size && !/^Standard_[A-Za-z0-9_]+$/.test(p.vm_size)) return 'VM size must look like Standard_D2s_v3'
  // with an explicit VM size the vCPU / memory are required; without one (Apprise) they are optional but must be valid
  const needed = !!p.vm_size
  if ((needed || v.vm_vcpu.trim()) && (p.vm_vcpu == null || !Number.isInteger(p.vm_vcpu) || p.vm_vcpu < 1)) return 'vCPU must be a whole number ≥ 1'
  if ((needed || v.vm_memory_gb.trim()) && (p.vm_memory_gb == null || p.vm_memory_gb <= 0)) return 'Memory (GB) must be greater than 0'
  // blank OS disk = the image's default size
  if (v.vm_os_disk_gb.trim() && (p.vm_os_disk_gb == null || !Number.isInteger(p.vm_os_disk_gb) || p.vm_os_disk_gb < 30 || p.vm_os_disk_gb > 4095))
    return 'OS disk must be between 30 and 4095 GB, or blank for the image default'
  if (!(VM_DISK_TYPES as readonly string[]).includes(p.vm_os_disk_type)) return 'Choose an OS disk type'
  if (!(VM_IDENTITY_MODES as readonly string[]).includes(p.vm_identity_default)) return 'Choose a default identity mode'
  return null
}

/** One-line summary for the Platform Config size rows. */
export function vmSummary(vm: VmSizing): string {
  const flags = [
    `public IP ${vm.public_ip_default ? 'on' : 'off'}`,
    `identity ${vm.identity_default}`,
    `monitoring ${vm.monitoring_default ? 'on' : 'off'}`,
    `backup ${vm.backup_default ? 'on' : 'off'}`,
  ]
  const derived = vm.derived ? ' · derived from vCPU / RAM' : ''
  const os = vm.image ? ` · OS: ${vm.image.label || `${vm.image.offer}:${vm.image.sku}`}` : ''
  return `VM: ${vm.vm_size}${os} · ${vm.vcpu} vCPU · ${vm.memory_gb} GB RAM · ${vm.os_disk_gb == null ? 'image-default' : `${vm.os_disk_gb} GB`} ${vm.os_disk_type} OS disk · ${flags.join(' · ')}${vm.overrides_allowed ? '' : ' · overrides locked'}${derived}`
}
